// This file can load more than once on the same page (e.g. main-product +
// featured-product sections together), and a plain top-level `class` throws
// on redeclaration the second time - unlike customElements.define, which
// already no-ops safely on its own. Nest everything under the same guard so
// a second load is inert instead of throwing.
if (!customElements.get('product-modal')) {
  // Pinch/tap-to-zoom + pan for a single <img> in the lightbox (see
  // ProductModal.activateZoom, which constructs one of these for every
  // active image at every breakpoint). Pointer events unify mouse and touch,
  // so the same class drives both: pinch never fires for a mouse (it needs
  // two simultaneous pointers) and drag-to-pan-while-zoomed already works
  // for a mouse the same way it does for a finger. The one thing that does
  // differ by input is the tap gesture itself - see isPreciseHoverDevice()/
  // registerTap() - a real mouse toggles zoom on a single click, while touch
  // keeps requiring a double-tap so a plain tap can still just dismiss/
  // browse. Panning/zoom state always resets to fit when a new image
  // becomes active - per spec there's no "remember zoom across navigation"
  // requirement - so this class only ever has to manage the single
  // currently-active image.
  function isPreciseHoverDevice() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  class ImageZoomPan {
    constructor(img, options = {}) {
      this.img = img;
      this.minScale = 1;
      this.maxScale = 3;
      this.tapZoomScale = 2.5;
      this.scale = 1;
      this.translateX = 0;
      this.translateY = 0;
      this.baseWidth = 0;
      this.baseHeight = 0;
      this.pointers = new Map();
      this.pinchStartDistance = 0;
      this.pinchStartScale = 1;
      this.panStart = null;
      this.swipeStart = null;
      this.lastTap = null;
      this.highResLoaded = false;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.onNavigate = options.onNavigate || (() => {});
      this.modalHost = options.modalHost || null;

      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerUp = this.handlePointerUp.bind(this);

      this.img.addEventListener('pointerdown', this.handlePointerDown);
      this.img.addEventListener('pointermove', this.handlePointerMove);
      this.img.addEventListener('pointerup', this.handlePointerUp);
      this.img.addEventListener('pointercancel', this.handlePointerUp);

      this.measureBase();
      this.applyTransform(false);
    }

    destroy() {
      this.img.removeEventListener('pointerdown', this.handlePointerDown);
      this.img.removeEventListener('pointermove', this.handlePointerMove);
      this.img.removeEventListener('pointerup', this.handlePointerUp);
      this.img.removeEventListener('pointercancel', this.handlePointerUp);
      this.img.style.transform = '';
      this.img.style.transition = '';
      this.img.style.touchAction = '';
      this.img.style.cursor = '';
      this.pointers.clear();
      this.modalHost?.classList.remove('is-zoomed');
    }

    isZoomed() {
      return this.scale > this.minScale + 0.001;
    }

    // Cached instead of read live during a gesture: getBoundingClientRect()
    // reflects whatever transform is already painted, so reading it
    // mid-pinch (before the new scale has been applied) would clamp
    // against last frame's size. Re-measured whenever the image is back at
    // fit, which is also the only time a new gesture can start from a
    // clean baseline.
    measureBase() {
      const rect = this.img.getBoundingClientRect();
      if (rect.width > 0) {
        this.baseWidth = rect.width;
        this.baseHeight = rect.height;
      }
    }

    // Loaded once, only once zoom actually starts - keeps the fit-view
    // image request small and avoids paying for the large asset on every
    // product page view.
    loadHighRes() {
      if (this.highResLoaded) return;
      const zoomSrc = this.img.dataset.zoomSrc;
      if (!zoomSrc || this.img.src === zoomSrc) return;
      this.highResLoaded = true;
      const preload = new Image();
      preload.onload = () => {
        this.img.src = zoomSrc;
      };
      preload.src = zoomSrc;
    }

    clamp(value, min, max) {
      return Math.min(max, Math.max(min, value));
    }

    getBounds() {
      return {
        maxX: Math.max(0, (this.baseWidth * this.scale - this.baseWidth) / 2),
        maxY: Math.max(0, (this.baseHeight * this.scale - this.baseHeight) / 2),
      };
    }

    clampTranslate() {
      const { maxX, maxY } = this.getBounds();
      this.translateX = this.clamp(this.translateX, -maxX, maxX);
      this.translateY = this.clamp(this.translateY, -maxY, maxY);
    }

    // Updates scale + translate so `point` (viewport coordinates) stays
    // visually stationary as scale changes to `nextScale` - i.e. zooms
    // centred on that point rather than on the image's fixed center.
    // Reads the current (pre-update) rect, so callers must call this
    // before anything else changes scale/translate this frame. Doesn't
    // paint - callers call applyTransform() themselves so pinch (continuous,
    // no transition) and double-tap (one animated jump) can pick their own
    // `animate` value.
    zoomAt(point, nextScale) {
      const rect = this.img.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const ratio = nextScale / this.scale;

      this.translateX += (point.x - centerX) * (1 - ratio);
      this.translateY += (point.y - centerY) * (1 - ratio);
      this.scale = nextScale;
      this.clampTranslate();
    }

    applyTransform(animate) {
      this.img.style.transition = animate && !this.reduceMotion ? 'transform 0.2s ease' : 'none';
      this.img.style.transform = `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`;
      // At fit, leave native vertical scroll/pull-to-refresh alone and let
      // a horizontal drag fall through to the swipe-to-navigate handling
      // below; once zoomed, claim the gesture entirely so panning doesn't
      // fight the page or the browser's own pinch-zoom.
      this.img.style.touchAction = this.isZoomed() ? 'none' : 'pan-y';
      // Cosmetic on touch (no cursor concept there), but signals click-to-
      // zoom/zoom-out on a mouse.
      this.img.style.cursor = this.isZoomed() ? 'zoom-out' : 'zoom-in';
      // Drives the desktop-only "click to zoom" hint icon (hidden once
      // zoomed) in section-main-product.css.
      this.modalHost?.classList.toggle('is-zoomed', this.isZoomed());
    }

    reset(animate = true) {
      this.scale = this.minScale;
      this.translateX = 0;
      this.translateY = 0;
      this.measureBase();
      this.applyTransform(animate);
    }

    handlePointerDown(event) {
      this.img.setPointerCapture(event.pointerId);
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.pointers.size === 2) {
        const [p1, p2] = Array.from(this.pointers.values());
        this.pinchStartDistance = Math.hypot(p2.x - p1.x, p2.y - p1.y);
        this.pinchStartScale = this.scale;
        this.panStart = null;
        this.swipeStart = null;
        return;
      }

      if (this.pointers.size === 1) {
        if (this.scale === this.minScale) this.measureBase();
        const point = { x: event.clientX, y: event.clientY };
        this.panStart = { ...point, translateX: this.translateX, translateY: this.translateY };
        this.swipeStart = this.isZoomed() ? null : point;
      }
    }

    handlePointerMove(event) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.pointers.size === 2) {
        event.preventDefault();
        const [p1, p2] = Array.from(this.pointers.values());
        const distance = Math.hypot(p2.x - p1.x, p2.y - p1.y);
        if (this.pinchStartDistance > 0) {
          const nextScale = this.clamp(
            this.pinchStartScale * (distance / this.pinchStartDistance),
            this.minScale,
            this.maxScale
          );
          if (nextScale > this.minScale) this.loadHighRes();
          const midpoint = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
          this.zoomAt(midpoint, nextScale);
          this.applyTransform(false);
        }
        return;
      }

      if (this.pointers.size === 1 && this.panStart && this.isZoomed()) {
        event.preventDefault();
        const point = this.pointers.get(event.pointerId);
        this.translateX = this.panStart.translateX + (point.x - this.panStart.x);
        this.translateY = this.panStart.translateY + (point.y - this.panStart.y);
        this.clampTranslate();
        this.applyTransform(false);
      }
    }

    handlePointerUp(event) {
      if (this.img.hasPointerCapture?.(event.pointerId)) {
        this.img.releasePointerCapture(event.pointerId);
      }

      const wasSinglePointer = this.pointers.size === 1;
      const point = this.pointers.get(event.pointerId);
      this.pointers.delete(event.pointerId);

      // Collapsing from a pinch back down to one finger - restart the pan
      // baseline from here instead of jumping using the stale two-finger
      // one.
      if (this.pointers.size === 1) {
        const [remaining] = Array.from(this.pointers.values());
        this.panStart = { x: remaining.x, y: remaining.y, translateX: this.translateX, translateY: this.translateY };
        this.swipeStart = null;
        return;
      }

      if (wasSinglePointer && point) {
        if (!this.isZoomed() && this.swipeStart) {
          const deltaX = point.x - this.swipeStart.x;
          const deltaY = point.y - this.swipeStart.y;
          if (Math.abs(deltaX) > 40 && Math.abs(deltaX) > Math.abs(deltaY)) {
            this.onNavigate(deltaX < 0 ? 1 : -1);
            this.panStart = null;
            this.swipeStart = null;
            return;
          }
        }

        if (this.panStart) {
          const moved = Math.hypot(point.x - this.panStart.x, point.y - this.panStart.y);
          if (moved < 10) this.registerTap(point);
        }
      }

      if (this.pointers.size === 0) {
        if (!this.isZoomed()) {
          this.scale = this.minScale;
          this.translateX = 0;
          this.translateY = 0;
        } else {
          this.clampTranslate();
        }
        this.applyTransform(true);
        this.panStart = null;
        this.swipeStart = null;
      }
    }

    // Viewport coordinates of the image's own midpoint - passing this to
    // zoomAt() is what makes a desktop click zoom in centred on the image
    // itself (translate delta works out to 0) rather than wherever was
    // clicked, so the zoomed-in view always starts centred/fit within the
    // box no matter where on the image you click.
    getCenterPoint() {
      const rect = this.img.getBoundingClientRect();
      return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    }

    registerTap(point) {
      // Mouse: a single click toggles zoom - always centred on the image
      // itself (not the click point), so it looks the same regardless of
      // where you click; or back out if already zoomed. You can still drag
      // to pan around once zoomed (see handlePointerMove). Touch: keep
      // requiring a double-tap (within 300ms and 30px of the first) so a
      // plain tap can still just dismiss/browse without accidentally
      // zooming, and keep zooming on the tap point to match the pinch
      // focal-point behavior.
      if (isPreciseHoverDevice()) {
        this.toggleZoomAt(this.getCenterPoint());
        return;
      }

      const now = Date.now();
      if (
        this.lastTap &&
        now - this.lastTap.time < 300 &&
        Math.hypot(point.x - this.lastTap.x, point.y - this.lastTap.y) < 30
      ) {
        this.toggleZoomAt(point);
        this.lastTap = null;
      } else {
        this.lastTap = { time: now, x: point.x, y: point.y };
      }
    }

    toggleZoomAt(point) {
      if (this.isZoomed()) {
        this.reset(true);
        return;
      }

      this.loadHighRes();
      this.zoomAt(point, this.tapZoomScale);
      this.applyTransform(true);
    }
  }

  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.currentZoom = null;
        this.bindNav();
      }

      bindNav() {
        this.prevButton = this.querySelector('[data-modal-prev]');
        this.nextButton = this.querySelector('[data-modal-next]');
        this.dotButtons = Array.from(this.querySelectorAll('[data-modal-dot]'));

        this.prevButton?.addEventListener('click', () => this.stepMedia(-1));
        this.nextButton?.addEventListener('click', () => this.stepMedia(1));
        this.dotButtons.forEach((button) => {
          button.addEventListener('click', () => this.goToMedia(button.dataset.mediaId));
        });
      }

      hide() {
        super.hide();
        // Per spec, closing resets zoom so reopening (even on the same
        // image) always starts at fit.
        this.currentZoom?.reset(false);
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      getMediaItems() {
        return Array.from(this.querySelectorAll('.product-media-modal__content > [data-media-id]'));
      }

      stepMedia(direction) {
        const items = this.getMediaItems();
        const activeIndex = items.findIndex((item) => item.classList.contains('active'));
        const nextIndex = activeIndex + direction;
        if (activeIndex === -1 || nextIndex < 0 || nextIndex >= items.length) return;
        this.goToMedia(items[nextIndex].dataset.mediaId);
      }

      goToMedia(mediaId) {
        const items = this.getMediaItems();
        const next = items.find((item) => item.dataset.mediaId === mediaId);
        if (!next) return;

        items.forEach((item) => item.classList.remove('active'));
        next.classList.add('active');
        this.activateMedia(next);
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        this.activateMedia(activeMedia);
      }

      // Shared by both the initial open (showActiveMedia) and arrow/dot
      // navigation (goToMedia): starts any deferred video, syncs the nav's
      // disabled/active state, and (re)attaches the zoom controller to
      // whichever image is now active - resetting zoom for the new one and
      // tearing down the previous image's listeners.
      activateMedia(activeMedia) {
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();

        this.syncNav(activeMedia);
        this.activateZoom(activeMedia);
      }

      syncNav(activeMedia) {
        if (!this.prevButton && !this.nextButton && !this.dotButtons.length) return;

        const items = this.getMediaItems();
        const index = items.indexOf(activeMedia);

        if (this.prevButton) this.prevButton.disabled = index <= 0;
        if (this.nextButton) this.nextButton.disabled = index === -1 || index >= items.length - 1;

        this.dotButtons.forEach((button) => {
          button.classList.toggle(
            'slider-counter__link--active',
            button.dataset.mediaId === activeMedia.dataset.mediaId
          );
        });
      }

      // Runs at every breakpoint - ImageZoomPan itself adapts pinch/pan vs.
      // click/double-tap-to-zoom per input (see isPreciseHoverDevice there).
      // has-image-zoom/is-zoomed drive the desktop-only "click to zoom" hint
      // icon in section-main-product.css.
      activateZoom(activeMedia) {
        this.currentZoom?.destroy();
        this.currentZoom = null;
        this.classList.toggle('has-image-zoom', activeMedia.tagName === 'IMG');

        if (activeMedia.tagName !== 'IMG') return;

        this.currentZoom = new ImageZoomPan(activeMedia, {
          modalHost: this,
          onNavigate: (direction) => this.stepMedia(direction),
        });
      }
    }
  );
}
